import { Alert, Stack } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ActivityPanelProps, NodeNameField, TableSelect } from "./ActivityNodePanels";
import DataPicker from "../DataPicker";
import FlussNodePanelField from "./FlussNodePanelField";
import FlussNodePanelShell from "./FlussNodePanelShell";
import {
    bindingForKind,
    removeBindingKind,
    updateBinding,
} from "./fluss-node-panel-utils";

export const DataStoreReferenceNodePanel = ({
    selection,
    modeler,
    data,
    canViewAdvanced,
    readOnly,
    onChanged,
}: ActivityPanelProps) => {
    const { t } = useTranslation();
    const [table, setTable] = useState(String(bindingForKind(selection.businessObject, "data_store")?.table || ""));

    useEffect(() => {
        setTable(String(bindingForKind(selection.businessObject, "data_store")?.table || ""));
    }, [selection.id, selection.businessObject]);

    const changeTable = (next: string) => {
        setTable(next);
        if (next) {
            updateBinding(selection, modeler, "data_store", { table: next });
        } else {
            removeBindingKind(selection, modeler, "data_store");
        }
        onChanged();
    };

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="data_store_reference"
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "info",
                label: t("fluss.node_panel.tab.info", { defaultValue: "Info" }),
                content: (
                    <Stack spacing={1.5}>
                        <NodeNameField selection={selection} modeler={modeler} readOnly={readOnly} onChanged={onChanged} />
                        <FlussNodePanelField label={String(t("fluss.node_panel.data_store.table", { defaultValue: "Linked table" }))}>
                            <TableSelect
                                value={table}
                                tables={data.tables}
                                label={String(t("fluss.node_panel.data_store.table", { defaultValue: "Linked table" }))}
                                readOnly={readOnly}
                                onChange={changeTable}
                            />
                        </FlussNodePanelField>
                        <Alert severity="info">
                            {t("fluss.node_panel.data_store.hint", {
                                defaultValue: "Documentation only: this link does not affect compilation, publishing or execution.",
                            })}
                        </Alert>
                    </Stack>
                ),
            }]}
        />
    );
};

export const DataObjectReferenceNodePanel = ({
    selection,
    modeler,
    canViewAdvanced,
    readOnly,
    onChanged,
}: ActivityPanelProps) => {
    const { t } = useTranslation();
    const raw = bindingForKind(selection.businessObject, "data_object")?.variableRef;
    const [value, setValue] = useState<string | undefined>(typeof raw === "string" && raw ? raw : undefined);

    useEffect(() => {
        const next = bindingForKind(selection.businessObject, "data_object")?.variableRef;
        setValue(typeof next === "string" && next ? next : undefined);
    }, [selection.id, selection.businessObject]);

    const changeValue = (next: unknown) => {
        const nextValue = typeof next === "string" && next ? next : undefined;
        setValue(nextValue);
        if (nextValue) {
            updateBinding(selection, modeler, "data_object", { variableRef: nextValue });
        } else {
            removeBindingKind(selection, modeler, "data_object");
        }
        onChanged();
    };

    const label = String(t("fluss.node_panel.data_object.variable", { defaultValue: "Linked variable or record field" }));

    return (
        <FlussNodePanelShell
            selection={selection}
            kind="data_object_reference"
            canViewAdvanced={canViewAdvanced}
            tabs={[{
                key: "info",
                label: t("fluss.node_panel.tab.info", { defaultValue: "Info" }),
                content: (
                    <Stack spacing={1.5}>
                        <NodeNameField selection={selection} modeler={modeler} readOnly={readOnly} onChanged={onChanged} />
                        <FlussNodePanelField label={label}>
                            <DataPicker
                                value={value}
                                onChange={changeValue}
                                allowFixed={false}
                                disabled={readOnly}
                                ariaLabel={label}
                            />
                        </FlussNodePanelField>
                        <Alert severity="info">
                            {t("fluss.node_panel.data_object.hint", {
                                defaultValue: "Documentation only: this link does not affect compilation, publishing or execution.",
                            })}
                        </Alert>
                    </Stack>
                ),
            }]}
        />
    );
};
